import { colord } from 'colord';
import { WIDGETBASE_CLASS } from 'constants/bounding-box';
import { PAGE_CONTAINER_CLASSNAME } from 'modules/Editor/Editor.config';
import { useCallback, useRef, useState } from 'react';
import { READONLY_WIDGET_ID_PREFIX } from 'widgets/Widget.config';
import { intersectRect } from 'widgets/WidgetLayout.helpers';

import * as htmlToImage from '../../libs/html-to-image/html-to-image';
import { calculateRatio, getAllCanvasRGB, parseRatioToScore, quantization } from './useColorContrastChecker.helpers';
import { Config, Options, ScoreType, UseColorContrastCheckerProps } from './useColorContrastChecker.types';

// TODO: This preset can move to a specific reset css helper
const createPresetCssStyle = () => {
  const cssText = `
    .ProseMirror table .selectedCell:after {
      background: transparent !important;
    }
  `;

  const style = document.createElement('style');
  style.textContent = cssText;

  return style;
};

/**
 * This hook is used to calculate the color contrast score of the target element.
 * It will return the score using the options.
 *
 * @returns
 */
export const useColorContrastChecker = (): UseColorContrastCheckerProps => {
  const [score, setScore] = useState<ScoreType[]>([]);
  const options = useRef<Options[]>([]);

  // This ref value prevents triggering multiple calculating at the same time.
  const isCalculating = useRef<boolean>(false);

  // Calculate the color contrast score using the options
  const calculateScore = async () => {
    if (isCalculating.current) return;
    isCalculating.current = true;

    // Canvas is the rendered page, so we can reuse the canvas if the multiple target(widget) is using the same page
    let canvasCache: any = {};

    // Calculate multiple promises in serial
    const runPromiseSerial = (tasks: Array<() => Promise<string>>) => {
      let result: Promise<string | void> = Promise.resolve();
      tasks.forEach((task) => {
        result = result.then(() => task());
      });
      return result;
    };

    if (!options?.current) return;

    const resultScoreList: any = [];
    const allTargetWidget = options?.current.reduce(
      (map, current) => map.set(current.widgetId, current),
      new Map<string, Options>(),
    );

    // If it has only 1 target and the pageRef is the page container, we can use the optimized single search
    // The optimized single search is faster than the normal color contrast checker
    // But it only works when the target is a page container (e.g. Color Picker)
    const useOptimizedSingleSearch =
      options?.current?.length === 1 &&
      options.current[0]?.targetPageRef?.classList?.contains(PAGE_CONTAINER_CLASSNAME);

    const promiseList: Array<() => Promise<string>> = options?.current?.map((option, index) => {
      return () =>
        new Promise(async (resolve, reject) => {
          const _pageId = option.pageId;
          const _widgetId = option.widgetId;
          const _targetPageRef = option.targetPageRef;
          const _rect = option.rect;
          const _fontSize = option?.fontSize ? Number.parseInt(option?.fontSize.toString()) : null;
          const _fontWeight = option?.fontWeight ?? null;
          const _color = option.color;
          const _pageWidthPx = option.pageWidthPx;
          const _pageHeightPx = option.pageHeightPx;

          if (!_targetPageRef || !_rect || !_color) return;

          if (!canvasCache.hasOwnProperty(_pageId)) {
            const targetPageBB = _targetPageRef.getBoundingClientRect();
            canvasCache[_pageId] = await htmlToImage.toCanvas(_targetPageRef, {
              filter: (node) => {
                // Edge case: table widget may insert an empty image tag
                if (node.tagName === 'IMG' && !node.getAttribute('src')) {
                  return false;
                }
                if (useOptimizedSingleSearch) {
                  // The widget that we are checking - filter using config
                  if (node.id === _widgetId) {
                    return !allTargetWidget.get(node.id)?.enabledFilter;
                  }

                  // Optimization: Filter out any widgets that do not intersect with the widget that we are computing
                  // These shouldn't be cloned as cloning is an expensive operation and can lead to lag for pages with many widgets.
                  if (node.classList?.contains(WIDGETBASE_CLASS) && typeof node.getBoundingClientRect === 'function') {
                    const nodeBB = node.getBoundingClientRect();
                    const currNodeRect = {
                      topPx: nodeBB.y - targetPageBB.y,
                      leftPx: nodeBB.x - targetPageBB.x,
                      widthPx: nodeBB.width,
                      heightPx: nodeBB.height,
                    };

                    const widgetRect = {
                      topPx: _rect.yPx,
                      leftPx: _rect.xPx,
                      widthPx: _rect.widthPx,
                      heightPx: _rect.heightPx,
                    };
                    return intersectRect(currNodeRect, widgetRect);
                  }
                }

                return !allTargetWidget.get(
                  node?.id?.startsWith(READONLY_WIDGET_ID_PREFIX)
                    ? node.id.replace(READONLY_WIDGET_ID_PREFIX, '')
                    : node.id,
                )?.enabledFilter;
              },
              filterText: (node) =>
                !allTargetWidget.get(
                  node?.id?.startsWith(READONLY_WIDGET_ID_PREFIX)
                    ? node.id.replace(READONLY_WIDGET_ID_PREFIX, '')
                    : node.id,
                )?.enabledTextFilter,
              presetCssStyle: createPresetCssStyle(),
              canvasWidth: _pageWidthPx / devicePixelRatio,
              canvasHeight: _pageHeightPx / devicePixelRatio,
            });
          }

          /**
           * Get all RGB values from the canvas
           */
          const rgbArray = getAllCanvasRGB(canvasCache[_pageId], _rect);

          /**
           * Color quantization
           * A process that reduces the number of colors used in an image
           * while trying to visually maintin the original image as much as possible
           */
          const quantColors = quantization(rgbArray, 1);

          /**
           * Return the score of the color contrast
           * Each quant color item is parsed to ratio number using targetcolor(_color.rgba)
           */
          const ratioList = calculateRatio(_color.rgba, quantColors);

          /**
           * The score is calculated by the ratio value and the font size and weight.
           */
          const resultScore = parseRatioToScore(ratioList, _fontSize, _fontWeight);
          const resultData = resultScore.sort((a: any, b: any) => b?.accuracy - a?.accuracy)?.[0];

          resultScoreList.push({
            pageId: _pageId,
            widgetId: _widgetId,
            score: resultData.score,
            ratio: resultData.ratio,
            accuracy: resultData.accuracy,
          });

          const isLastTask = options.current.length === resultScoreList.length;
          if (isLastTask) {
            options.current = [];
            setScore(resultScoreList);
            isCalculating.current = false;
          }

          resolve('success');
        });
    });

    if (!promiseList.length) {
      setScore([]);
      isCalculating.current = false;
      return;
    }

    runPromiseSerial(promiseList);
  };

  const setOptions = ({
    pageId,
    widgetId,
    targetPage,
    targetColor,
    targetRect,
    targetFontSize,
    targetFontWeight,
    pageWidthPx,
    pageHeightPx,
    enabledFilter = true,
    enabledTextFilter = false,
  }: Config): void => {
    if (isCalculating.current) return;

    const newOptions: Options = {
      pageId,
      widgetId,
      targetPageRef: targetPage,
      color: colord(targetColor),
      rect: targetRect,
      fontSize: targetFontSize ?? null,
      fontWeight: targetFontWeight ?? null,
      pageWidthPx,
      pageHeightPx,
      enabledFilter,
      enabledTextFilter,
    };

    options.current.push(newOptions);
  };

  const resetScore = useCallback(() => {
    setScore([]);
  }, []);

  return [score, calculateScore, setOptions, resetScore];
};
